import { apiRequestPaged, buildURL } from './commons/CommonService.Functions';

// models
import { ProviderImportJob } from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ErrorType } from '../models/HttpError';
import { IPagedResult } from '../models/HttpPagedResult';

/**
 * Jobs de importación de consumos de proveedor en segundo plano (spec 008, MedicalProtocols 030).
 * TODAS las lecturas de `import-jobs` están acá (el POST que crea el job sigue en
 * ProviderConsumptionImport.Services). El gateway mapea /protocols/{everything} → MedicalProtocols API.
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const ProvidersURL = () => `${Gateway}protocols/providers`;

/** Cuántos jobs trae el historial del proveedor. */
export const IMPORT_JOBS_HISTORY_TAKE = 20;

/** Envoltura con `succeeded: false` (200 con errores de negocio) → ErrorType con los `errors[]` de la API. */
const unwrap = <T>(result: IPagedResult<T> | undefined): IPagedResult<T> | undefined => {
  if (result && result.succeeded === false) {
    const error: ErrorType = { ErrorCode: 200, ErrorMessage: result.message || 'Operation failed', Errors: result.errors ?? [] };
    throw error;
  }
  return result;
};

const read = async <T>(url: string, token: string, context: string): Promise<T | undefined> =>
  unwrap(await apiRequestPaged<T>(url, token, context))?.data ?? undefined;

/** GET providers/consumptions/import-jobs/{jobId} */
export const ImportJobURL = (jobId: string): string => buildURL(ProvidersURL(), 'consumptions', 'import-jobs', jobId);

/** GET providers/{ProviderId:int}/consumptions/import-jobs?take= */
export const ProviderImportJobsURL = (providerId: number, take: number = IMPORT_JOBS_HISTORY_TAKE): string =>
  `${buildURL(ProvidersURL(), providerId, 'consumptions', 'import-jobs')}?take=${encodeURIComponent(String(take))}`;

/** GET providers/consumptions/import-jobs/mine?activeOrSince={ISO8601} */
export const MyImportJobsURL = (activeOrSince: string): string =>
  `${buildURL(ProvidersURL(), 'consumptions', 'import-jobs', 'mine')}?activeOrSince=${encodeURIComponent(activeOrSince)}`;

/** Un job por id (seguimiento del paso 3 del asistente). */
export const GetImportJob = async (token: string, jobId: string): Promise<ProviderImportJob | undefined> =>
  read<ProviderImportJob>(ImportJobURL(jobId), token, 'GetImportJob');

/** Jobs del proveedor, más nuevos primero (historial y banner de la grilla). */
export const GetProviderImportJobs = async (token: string, providerId: number, take: number = IMPORT_JOBS_HISTORY_TAKE): Promise<ProviderImportJob[]> =>
  (await read<ProviderImportJob[]>(ProviderImportJobsURL(providerId, take), token, 'GetProviderImportJobs')) ?? [];

/** Jobs del usuario autenticado activos o terminados después de `activeOrSince` (aviso global). */
export const GetMyImportJobs = async (token: string, activeOrSince: string): Promise<ProviderImportJob[]> =>
  (await read<ProviderImportJob[]>(MyImportJobsURL(activeOrSince), token, 'GetMyImportJobs')) ?? [];
